import rawCatalog from '../assets/floor_catalog.json';

/**
 * Couches de sol de l'éditeur de cartes (table `map_floor_layers`) — généré
 * par `tools/assets-cli/src/ingest-floors.ts`.
 *
 * - Couche `floor` (« Sol ») : sols intérieurs (sans raccord) et terrains
 *   extérieurs, raccordés deux à deux façon Tiled (voir `pairs`).
 * - Couche `overlay` (« Revêtements ») : ce qui se pose sur n'importe quel
 *   sol (pelouses bordées, piscine).
 */
export type FloorLayerName = 'floor' | 'overlay';
export const FLOOR_LAYERS: FloorLayerName[] = ['floor', 'overlay'];

/** `blocking` : les avatars ne marchent pas dessus (eau) — voir isFloorCellBlocking(). */
export type FloorMaterial = (
  | { id: string; label: string; group: 'interior' | 'exterior'; mode: 'pattern'; w: number; h: number; frames: number[] }
  | { id: string; label: string; group: 'interior' | 'exterior'; mode: 'random'; frames: number[]; weights: number[] }
) & {
  blocking?: boolean;
  /** Jamais peint directement (sable mouillé : déduit, voir `wet`) — hors palette. */
  hidden?: boolean;
  /**
   * Couples de raccord (jeux autotile LimeZu) : ID du terrain extérieur → 48
   * frames au gabarit GameMaker. Une case dont un voisin est l'un de ces
   * terrains prend la pièce de raccord correspondante.
   */
  pairs?: Record<string, number[]>;
  /** Devient le terrain `id` à 2 cases ou moins (à travers lui-même) d'un terrain `near`. */
  wet?: { id: string; near: string[] };
};

export interface FloorTerrain {
  id: string;
  label: string;
  /** 48 frames dans l'ordre du gabarit GameMaker (frame 0 = vide). */
  frames: number[];
  blocking?: boolean;
  /**
   * Jeu inversé : les 47 pièces dessinent le TROU, le terrain est autour.
   * La case peinte affiche `fill` (tuile unie) et la bordure est dessinée
   * dans les cases voisines, sur une couche à part — overlayFringeFrame().
   */
  inverted?: boolean;
  fill?: number;
}

interface FloorCatalogFile {
  atlas: { url: string; tileSize: number; columns: number };
  materials: FloorMaterial[];
  terrains: FloorTerrain[];
}

const catalog = rawCatalog as FloorCatalogFile;

/** Atlas à charger en image Phaser (tileset d'une tilemap vierge). */
export const FLOOR_ATLAS = catalog.atlas;
export const floorMaterials = catalog.materials;
export const floorTerrains = catalog.terrains;

const materialById = new Map(floorMaterials.map((m) => [m.id, m]));
const terrainById = new Map(floorTerrains.map((t) => [t.id, t]));

export function findFloorMaterial(id: string): FloorMaterial | undefined {
  return materialById.get(id);
}

export function findFloorTerrain(id: string): FloorTerrain | undefined {
  return terrainById.get(id);
}

/** Un ID est-il peignable sur cette couche ? (validation API + éditeur) */
export function isValidFloorValue(layer: FloorLayerName, id: string): boolean {
  if (layer === 'overlay') return terrainById.has(id);
  const material = materialById.get(id);
  return material !== undefined && !material.hidden;
}

// ── Matériaux (couche Sol) ────────────────────────────────────

/** Hash déterministe d'une case — même variante de gazon à chaque rendu, sur tous les clients. */
function cellHash(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

export function materialFrame(material: FloorMaterial, x: number, y: number): number {
  if (material.mode === 'pattern') {
    const px = ((x % material.w) + material.w) % material.w;
    const py = ((y % material.h) + material.h) % material.h;
    return material.frames[py * material.w + px]!;
  }
  const total = material.weights.reduce((a, b) => a + b, 0);
  let r = cellHash(x, y) % total;
  for (let i = 0; i < material.frames.length; i++) {
    r -= material.weights[i]!;
    if (r < 0) return material.frames[i]!;
  }
  return material.frames[0]!;
}

// ── Terrains à raccords (couche Revêtements) ─────────────────

// Bits de voisinage : N=1 E=2 S=4 W=8 NE=16 SE=32 SW=64 NW=128. Un coin ne
// compte que si ses deux côtés adjacents sont du même terrain (autotile
// « blob » 47 pièces).
const N = 1, E = 2, S = 4, W = 8, NE = 16, SE = 32, SW = 64, NW = 128;

/**
 * Configuration de voisins → position dans le gabarit GameMaker (12×4).
 * Déduite des pixels des jeux LimeZu (bords/coins de chaque tuile : terrain
 * ou extérieur), identique sur les 12 jeux décodables sans ambiguïté — c'est
 * le gabarit standard, commun aux 26 jeux (2026-09-26).
 */
const BLOB_INDEX: Record<number, number> = {
  255: 1, 127: 2, 239: 3, 111: 4, 223: 5, 95: 6, 207: 7, 79: 8, 191: 9, 63: 10, 175: 11, 47: 12,
  159: 13, 31: 14, 143: 15, 15: 16, 55: 17, 39: 18, 23: 19, 7: 20, 110: 21, 78: 22, 46: 23, 14: 24,
  205: 25, 141: 26, 77: 27, 13: 28, 155: 29, 27: 30, 139: 31, 11: 32, 5: 33, 10: 34, 38: 35, 6: 36,
  76: 37, 12: 38, 137: 39, 9: 40, 19: 41, 3: 42, 4: 43, 2: 44, 1: 45, 8: 46, 0: 47,
};

function blobMask(same: (dx: number, dy: number) => boolean): number {
  const n = same(0, -1), e = same(1, 0), s = same(0, 1), w = same(-1, 0);
  let mask = (n ? N : 0) | (e ? E : 0) | (s ? S : 0) | (w ? W : 0);
  if (n && e && same(1, -1)) mask |= NE;
  if (s && e && same(1, 1)) mask |= SE;
  if (s && w && same(-1, 1)) mask |= SW;
  if (n && w && same(-1, -1)) mask |= NW;
  return mask;
}

/** Frame d'un terrain selon ses 8 voisins (`same(dx, dy)` : la case voisine porte-t-elle le même terrain ?). */
export function terrainFrame(terrain: FloorTerrain, same: (dx: number, dy: number) => boolean): number {
  if (terrain.inverted) return terrain.fill ?? 0;
  return terrain.frames[BLOB_INDEX[blobMask(same)]!]!;
}

const NEIGHBOURS: Array<[number, number]> = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, -1], [1, 1], [-1, 1], [-1, -1]];

/**
 * Bordure d'un terrain inversé dessinée DANS une case voisine (couche à part,
 * par-dessus les revêtements). Pour un jeu inversé, le « terrain » du
 * gabarit est le trou : on calcule donc le masque de la case voisine avec
 * « même terrain » = « PAS ce terrain inversé » — les côtés qui touchent la
 * pelouse reçoivent ses touffes d'herbe. 0 si aucun voisin inversé (ou si la
 * case elle-même en est un). Premier terrain inversé trouvé si plusieurs.
 */
export function overlayFringeFrame(layer: FloorLayerData, x: number, y: number): number {
  const own = floorValueAt(layer, x, y);
  if (own !== null && findFloorTerrain(own)?.inverted) return 0;
  for (const [dx, dy] of NEIGHBOURS) {
    const v = floorValueAt(layer, x + dx, y + dy);
    const terrain = v === null ? undefined : findFloorTerrain(v);
    if (!terrain?.inverted) continue;
    const mask = blobMask((ddx, ddy) => floorValueAt(layer, x + ddx, y + ddy) !== v);
    return terrain.frames[BLOB_INDEX[mask]!]!;
  }
  return 0;
}

// ── Données d'une couche ─────────────────────────────────────

/**
 * Stockage d'une couche : grille dense, une valeur par case — 0 = vide,
 * k = palette[k-1]. La palette contient des IDs de catalogue (stables), la
 * grille des petits entiers : ~45 Ko pour une carte 150×150 pleine.
 */
export interface FloorLayerData {
  width: number;
  height: number;
  palette: string[];
  cells: number[];
}

export interface FloorEdit {
  x: number;
  y: number;
  /** ID de matériau/terrain, ou null pour effacer. */
  value: string | null;
}

export const MAX_FLOOR_DIMENSION = 150;

export function emptyFloorLayer(width: number, height: number): FloorLayerData {
  return { width, height, palette: [], cells: new Array(width * height).fill(0) };
}

export function floorValueAt(layer: FloorLayerData, x: number, y: number): string | null {
  if (x < 0 || y < 0 || x >= layer.width || y >= layer.height) return null;
  const v = layer.cells[y * layer.width + x]!;
  return v === 0 ? null : layer.palette[v - 1] ?? null;
}

/**
 * Applique des modifications (partagé API/éditeur — même résultat des deux
 * côtés). Agrandit la grille si besoin (jusqu'à MAX_FLOOR_DIMENSION), ignore
 * les cases hors limites. Retourne une nouvelle couche, palette compactée.
 */
export function applyFloorEdits(layer: FloorLayerData, edits: FloorEdit[], minWidth = 0, minHeight = 0): FloorLayerData {
  let width = Math.min(MAX_FLOOR_DIMENSION, Math.max(layer.width, minWidth));
  let height = Math.min(MAX_FLOOR_DIMENSION, Math.max(layer.height, minHeight));
  for (const e of edits) {
    if (e.x >= 0 && e.y >= 0 && e.x < MAX_FLOOR_DIMENSION && e.y < MAX_FLOOR_DIMENSION && e.value !== null) {
      width = Math.max(width, e.x + 1);
      height = Math.max(height, e.y + 1);
    }
  }

  const values: Array<string | null> = new Array(width * height).fill(null);
  for (let y = 0; y < layer.height && y < height; y++) {
    for (let x = 0; x < layer.width && x < width; x++) values[y * width + x] = floorValueAt(layer, x, y);
  }
  for (const e of edits) {
    if (e.x < 0 || e.y < 0 || e.x >= width || e.y >= height) continue;
    values[e.y * width + e.x] = e.value;
  }

  const palette: string[] = [];
  const index = new Map<string, number>();
  const cells = values.map((v) => {
    if (v === null) return 0;
    let i = index.get(v);
    if (i === undefined) {
      palette.push(v);
      i = palette.length;
      index.set(v, i);
    }
    return i;
  });
  return { width, height, palette, cells };
}

/**
 * Une case de sol peint bloque-t-elle les avatars ? Le revêtement décide
 * s'il y en a un (sable peint sur de l'eau = rive praticable) ; sinon le sol.
 * Les objets posés « au sol » par-dessus (ponts, pontons) sont gérés par
 * l'appelant (ZoneScene), qui retire leurs cases.
 */
export function isFloorCellBlocking(
  floor: FloorLayerData | undefined,
  overlay: FloorLayerData | undefined,
  x: number,
  y: number,
): boolean {
  const terrainId = overlay ? floorValueAt(overlay, x, y) : null;
  if (terrainId !== null) return findFloorTerrain(terrainId)?.blocking === true;
  const materialId = floor ? floorValueAt(floor, x, y) : null;
  return materialId !== null && findFloorMaterial(materialId)?.blocking === true;
}

/**
 * Cache de valeurs effectives d'une couche Sol, le temps d'un rendu : la
 * frame d'une case dépend de ses voisines, dont le sable mouillé dépend
 * lui-même de ses voisines. Un nouveau cache à chaque rendu (Map vide).
 */
export type FloorFrameCache = Map<number, string | null>;

/**
 * Valeur « effective » d'une case de la couche Sol : la valeur peinte, sauf
 * pour un terrain `wet` (sable) qui devient sa variante mouillée à 1 case
 * d'un terrain `near` (eau), ou à 1 case d'une case de sable elle-même à
 * 1 case de l'eau — une bande de 2 cases, qui ne traverse que le sable.
 */
export function effectiveFloorValue(layer: FloorLayerData, x: number, y: number, cache?: FloorFrameCache): string | null {
  const key = y * MAX_FLOOR_DIMENSION + x;
  const cached = cache?.get(key);
  if (cached !== undefined) return cached;
  const value = floorValueAt(layer, x, y);
  let result = value;
  const wet = value === null ? undefined : materialById.get(value)?.wet;
  if (wet) {
    const touches = (cx: number, cy: number) => NEIGHBOURS.some(([dx, dy]) => {
      const v = floorValueAt(layer, cx + dx, cy + dy);
      return v !== null && wet.near.includes(v);
    });
    if (touches(x, y) || NEIGHBOURS.some(([dx, dy]) => floorValueAt(layer, x + dx, y + dy) === value && touches(x + dx, y + dy))) {
      result = wet.id;
    }
  }
  cache?.set(key, result);
  return result;
}

/** Frame de l'atlas à afficher pour une case (0 = rien). */
export function floorCellFrame(
  layerName: FloorLayerName, layer: FloorLayerData, x: number, y: number, cache: FloorFrameCache = new Map(),
): number {
  if (layerName === 'floor') {
    const value = effectiveFloorValue(layer, x, y, cache);
    if (value === null) return 0;
    const material = findFloorMaterial(value);
    if (!material) return 0;
    if (!material.pairs) return materialFrame(material, x, y);
    // Terrain extérieur retenu : le voisin le plus fréquent pour lequel un
    // couple existe (à égalité, l'ordre des couples du catalogue). Les autres
    // voisins comptent comme « même terrain » : bord net, jamais faux.
    const counts = new Map<string, number>();
    for (const [dx, dy] of NEIGHBOURS) {
      const n = effectiveFloorValue(layer, x + dx, y + dy, cache);
      if (n !== null && n !== value && material.pairs[n]) counts.set(n, (counts.get(n) ?? 0) + 1);
    }
    let outer: string | null = null;
    for (const id of Object.keys(material.pairs)) {
      if ((counts.get(id) ?? 0) > (outer ? counts.get(outer)! : 0)) outer = id;
    }
    if (outer === null) return materialFrame(material, x, y);
    const frames = material.pairs[outer]!;
    return frames[BLOB_INDEX[blobMask((dx, dy) => effectiveFloorValue(layer, x + dx, y + dy, cache) !== outer)]!]!;
  }
  const value = floorValueAt(layer, x, y);
  if (value === null) return 0;
  const terrain = findFloorTerrain(value);
  if (!terrain) return 0;
  return terrainFrame(terrain, (dx, dy) => floorValueAt(layer, x + dx, y + dy) === value);
}
